import React, { useEffect, useRef } from 'react'
import { FetchResult } from '@apollo/client'
import gql from 'graphql-tag'
import { apolloClient } from 'lib/core/services/magento/client'
import { useCart } from 'lib/core/hooks/useCart'
import { useCheckout } from 'lib/modules/checkout'

const SANDBOX = 'https://pilot-payflowlink.paypal.com'
const IFRAME_ID = 'payflowProIframeTransparent'

type HandlePayflowProResponseMutation = (payload: string) => Promise<FetchResult<{
  handlePayflowProResponse: {
    cart: {
      email: string
    }
  }
}>>

export interface IframeFormProps {
  onSuccess: () => void
  onError: (message?: string) => void
  ccData?: {
    number: string
    expMonth: string
    expYear: string
    cvv: string
  }
  tokens?: {
    response_message: string
    iframe_url: string
    result: string
    result_code: string
    secure_token: string
    secure_token_id: string
  }
}

export const IframeForm = ({
  ccData,
  tokens,
  onSuccess,
  onError
}: IframeFormProps) => {
  const form: any = useRef(null)
  const iframe: any = useRef(null)

  const { cartId } = useCart()
  const { lockCheckout, unlockCheckout } = useCheckout()
  const secureTokenId = tokens?.secure_token_id

  const handlePayflowProResponse: HandlePayflowProResponseMutation = async (payload) => {
    const query = `
      mutation {
        handlePayflowProResponse (
          input: {
            cart_id: "${cartId}",
            paypal_payload: "${payload}"
          }
        ) {
          cart {
            email
          }
        }
      }
    `

    return apolloClient.mutate({ mutation: gql`${query}` })
  }

  useEffect(() => {
    if (secureTokenId && form.current?.submit) {
      lockCheckout()
      form.current?.submit?.()
    }
    // TODO: fix this
    // eslint-disable-next-line
  }, [
    secureTokenId
  ])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes(process.env.REACT_APP_MAGENTO_URI as string)) {
        return
      }

      const data: { response_payload: string, error: string | null } = JSON.parse(event.data)

      if (data.error) {
        unlockCheckout()
        onError(data.error)
        return
      }

      if (!data.response_payload) {
        return
      }

      handlePayflowProResponse(data.response_payload).then((res) => {
        unlockCheckout()
        res.errors?.length ? onError() : onSuccess()
      }).catch(() => {
        unlockCheckout()
        onError()
        // TODO: report error / send error message?
      })
    }
    window.addEventListener('message', handleMessage, false)
    return () => {
      window.removeEventListener('message', handleMessage, false)
    }
    // TODO: fix this
    // eslint-disable-next-line
  }, [
    secureTokenId
  ])

  if (!tokens || !ccData) {
    return null
  }

  return (
    <>
      <form
        ref={form}
        hidden
        target={IFRAME_ID}
        action={tokens?.iframe_url ?? SANDBOX}
        method='POST'
        encType='application/x-www-form-urlencoded'
      >
        <input value={tokens.result} name='result' type='hidden' />
        <input value={tokens.secure_token} name='securetoken' type='hidden' />
        <input value={tokens.secure_token_id} name='securetokenid' type='hidden' />
        <input value={tokens.response_message} name='respmsg' type='hidden' />
        <input value={tokens.result_code} name='result_code' type='hidden' />
        <input value={ccData.cvv} name='csc' type='hidden' />
        <input value={`${ccData.expMonth}${ccData.expYear}`} name='expdate' type='hidden' />
        <input value={ccData.number} name='acct' type='hidden' />
      </form>
      <iframe
        style={{ position: 'absolute', visibility: 'hidden', height: '0', width: '0', border: 'none' }}
        title='PayflowPro'
        onLoad={(e) => {
          iframe?.current.contentWindow.postMessage('PayflowPro', '*')
        }}
        ref={iframe}
        name={IFRAME_ID}
        id={IFRAME_ID}
      />
    </>
  )
}
